import { http } from '../request'
import type {
    PaymentOrderListRequest,
    PaymentOrderMySalesRequest,
    PaymentCreateRentalRequest,
    PaymentBuyerCompleteRequest,
    PaymentSellerConfirmRequest,
    PaymentUserCancelOrderRequest,
    PaymentFinalConfirmRequest,
    PaymentCancelRefundRequest,
    PaymentOrderListResponse,
    PaymentOrderMySalesResponse,
    PaymentOrderDetailResponse,
    PaymentCreateRentalResponse,
    PaymentBuyerCompleteResponse,
    PaymentSellerConfirmResponse,
    PaymentUserCancelOrderResponse,
    PaymentFinalConfirmResponse,
    PaymentCancelRefundResponse,
    PaymentCheckStatusResponse,
    PaymentOrderListItem,
    OrderStatisticsResponse,
    AlipayPaymentResponse,
    FubeiAuthUrlRequest,
    FubeiAuthUrlResponse,
    FubeiPaymentRequest,
    FubeiPaymentResponse
} from '../types/payments'

/**
 * 支付模块相关 API
 */
export const paymentsApi = {
    /**
     * 获取当前用户的支付订单列表
     * GET /api/payments/orders/
     */
    getPaymentOrderList(params?: PaymentOrderListRequest): Promise<PaymentOrderListResponse> {
        return http.list<PaymentOrderListItem>('/api/payments/orders/', params)
    },

    /**
     * 获取订单详情
     * GET /api/payments/orders/{id}/
     */
    getPaymentOrderDetail(id: number): Promise<PaymentOrderDetailResponse> {
        return http.get<PaymentOrderDetailResponse>(`/api/payments/orders/${id}/`)
    },

    /**
     * 创建新的游戏账号租赁订单
     * POST /api/payments/orders/create-rental/
     */
    createRentalOrder(data: PaymentCreateRentalRequest): Promise<PaymentCreateRentalResponse> {
        return http.post<PaymentCreateRentalResponse>('/api/payments/orders/create-rental/', data)
    },

    /**
     * 获取我作为卖家的销售订单列表
     * GET /api/payments/orders/
     */
    getMySalesOrderList(params?: PaymentOrderMySalesRequest): Promise<PaymentOrderMySalesResponse> {
        return http.list<PaymentOrderListItem>('/api/payments/orders/', params)
    },

    /**
     * 租客确认完成租赁
     * POST /api/payments/orders/{id}/buyer-complete/
     */
    buyerCompleteRental(id: number, data?: PaymentBuyerCompleteRequest): Promise<PaymentBuyerCompleteResponse> {
        return http.post<PaymentBuyerCompleteResponse>(`/api/payments/orders/${id}/buyer-complete/`, data)
    },

    /**
     * 卖家确认账号归还
     * POST /api/payments/orders/{id}/seller-confirm/
     */
    sellerConfirmReturn(id: number, data: PaymentSellerConfirmRequest): Promise<PaymentSellerConfirmResponse> {
        return http.post<PaymentSellerConfirmResponse>(`/api/payments/orders/${id}/seller-confirm/`, data)
    },

    /**
     * 用户取消自己的订单
     * POST /api/payments/orders/{id}/cancel-order/
     */
    cancelOrder(id: number, data?: PaymentUserCancelOrderRequest): Promise<PaymentUserCancelOrderResponse> {
        return http.post<PaymentUserCancelOrderResponse>(`/api/payments/orders/${id}/cancel-order/`, data)
    },

    /**
     * 取消退款申请
     * POST /api/payments/orders/{id}/cancel-refund/
     */
    cancelRefund(id: number, data?: PaymentCancelRefundRequest): Promise<PaymentCancelRefundResponse> {
        return http.post<PaymentCancelRefundResponse>(`/api/payments/orders/${id}/cancel-refund/`, data)
    },

    /**
     * 最终确认交易完成
     * POST /api/payments/orders/{id}/final-confirm/
     */
    finalConfirm(id: number, data?: PaymentFinalConfirmRequest): Promise<PaymentFinalConfirmResponse> {
        return http.post<PaymentFinalConfirmResponse>(`/api/payments/orders/${id}/final-confirm/`, data)
    },

    /**
     * 检查订单支付状态（用于前端轮询）
     * GET /api/payments/orders/{id}/check-payment-status/
     */
    checkPaymentStatus(id: number): Promise<PaymentCheckStatusResponse> {
        return http.get<PaymentCheckStatusResponse>(`/api/payments/orders/${id}/check-payment-status/`)
    },

    /**
     * 使用余额支付订单
     * POST /api/payments/orders/{id}/pay-by-balance/
     */
    payByBalance(id: number): Promise<PaymentOrderDetailResponse> {
        return http.post<PaymentOrderDetailResponse>(`/api/payments/orders/${id}/pay-by-balance/`)
    },

    /**
     * 使用支付宝支付订单（新版统一接口）
     * POST /api/payments/orders/{id}/pay-by-alipay/
     * 建议显式传递 trade_type：PC 传 'page'，移动端传 'wap'
     */
    payByAlipay(id: number, data: {
        trade_type?: 'page' | 'wap'  // 可选，兼容性字段
    } = {}): Promise<AlipayPaymentResponse> {
        return http.post<AlipayPaymentResponse>(`/api/payments/orders/${id}/pay-by-alipay/`, data)
    },

    /**
     * 使用富呗支付订单
     * POST /api/payments/orders/{id}/pay-by-fubei/
     */
    payByFubei(id: number, data: FubeiPaymentRequest = {}): Promise<FubeiPaymentResponse> {
        return http.post<FubeiPaymentResponse>(`/api/payments/orders/${id}/pay-by-fubei/`, data)
    },

    /**
     * 使用富呗聚合扫码支付订单
     * POST /api/payments/orders/{id}/pay-by-fubei-qr/
     */
    payByFubeiQr(id: number): Promise<FubeiPaymentResponse> {
        return http.post<FubeiPaymentResponse>(`/api/payments/orders/${id}/pay-by-fubei-qr/`)
    },

    /**
     * 获取富呗支付宝授权地址
     * POST /api/payments/orders/{id}/fubei-alipay-auth-url/
     */
    getFubeiAlipayAuthUrl(id: number, data: FubeiAuthUrlRequest = {}): Promise<FubeiAuthUrlResponse> {
        return http.post<FubeiAuthUrlResponse>(`/api/payments/orders/${id}/fubei-alipay-auth-url/`, data)
    },

    /**
     * 删除订单（软删除）
     * DELETE /api/payments/orders/{id}/
     */
    deleteOrder(id: number): Promise<void> {
        return http.delete(`/api/payments/orders/${id}/`)
    },

    /**
     * 获取当前用户的订单统计数据
     * GET /api/payments/orders/statistics/
     */
    getOrderStatistics(): Promise<OrderStatisticsResponse> {
        return http.get<OrderStatisticsResponse>('/api/payments/orders/statistics/')
    },

    /**
     * 买家拒绝卖家提交的消耗物
     * POST /api/payments/orders/{id}/buyer-reject-consumption/
     */
    buyerRejectConsumption(id: number, data?: { reason?: string }): Promise<void> {
        return http.post<void>(`/api/payments/orders/${id}/buyer-reject-consumption/`, data)
    }
}

export default paymentsApi
